import Field, { FieldKey, getInputForUpdate } from "../commons/model/form/Field";
import FormWithSections, { FormSection } from "../commons/components/form/FormWithSections";
import FormTitle from "../commons/components/form/FormTitle";
import Form from "../commons/components/form/Form";
import * as React from "react";
import { useState } from "react";
import { getPayload } from "../commons/utils/form-utils";
import sfetch from "../commons/utils/super-fetch";
import { SectionConfiguration } from "../commons/components/form/SectionConfiguration";
import { getCountryFromPhoneNumberOrDefault } from "../commons/utils/phoneNumber";
import { Gender, getI18n } from "../commons/model/patient/Gender";
import { findInformation, PatientInformation } from "../commons/model/patient/PatientInfo";
import Country from "../commons/model/address/Country";
import SelectableOption from "../commons/model/SelectableOption";
import { Info } from "../commons/components/Alerts";
import { ErrorCode } from "../commons/utils/errorCode";
import { sanitize } from "../commons/utils/sanitize";
import InputState from "../commons/model/form/InputState";

export default function PersonalInfoForm({
  patientInformation,
  fields,
  availableCountries,
  updateUrl,
  showPhoneDisclaimer,
  showSomePersonalInfosNonEditableDisclaimer,
  title,
  submitLabel,
  afterSubmit,
}: {
  patientInformation: PatientInformation;
  fields: Field[];
  availableCountries: Country[];
  updateUrl: string;
  showPhoneDisclaimer: boolean;
  showSomePersonalInfosNonEditableDisclaimer: boolean;
  title?: string;
  submitLabel?: string;
  afterSubmit?: (form: { [key: string]: InputState }) => any;
}) {
  const [errorMessage, setErrorMessage] = useState(null);

  const submitPatientInformation = async (form: { [key: string]: InputState }) => {
    const payload = getPayload(
      form,
      // exception for email due to modifiable email on unregistered user only
      fields.filter((f) => f.name === "email" || !f.disabled || f.mandatory).map((f) => f.name)
    );

    try {
      await sfetch(updateUrl, {
        method: "PUT",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (afterSubmit) afterSubmit(form);
    } catch (error) {
      let jsonError;
      try {
        jsonError = await error.json();
      } catch (e) {
        jsonError = {};
      }
      if (jsonError.code == ErrorCode.BAD_PHONE_NUMBER) {
        setErrorMessage(jsonError.message);
      } else {
        setErrorMessage(i18n["Global.TechnicalError"]);
      }

      throw error;
    }
  };

  const sections: SectionConfiguration[] = [
    {
      title: i18n["Form.PersonalInfo.Section.Personal"],
      fieldKeys: [
        FieldKey.FIRST_NAME,
        FieldKey.MIDDLE_NAME,
        FieldKey.LAST_NAME,
        FieldKey.PREFERRED_NAME,
        FieldKey.BIRTH_DATE,
        FieldKey.GENDER,
        FieldKey.SSN,
      ],
      topContent: showSomePersonalInfosNonEditableDisclaimer ? (
        <Info>{i18n["Form.PersonalInfo.Disclaimer.SomePersonalInfosNonEditable"]}</Info>
      ) : null,
    },
    {
      title: i18n["Form.PersonalInfo.Section.Contact"],
      fieldKeys: [
        FieldKey.EMAIL,
        FieldKey.CELL_PHONE_NUMBER,
        FieldKey.WORK_PHONE_NUMBER,
        FieldKey.HOME_PHONE_NUMBER,
      ],
      additionalContent: showPhoneDisclaimer ? (
        <div
          className="additional-content small"
          dangerouslySetInnerHTML={{
            __html: sanitize(i18n["Form.PersonalInfo.Disclaimer.Phone"]),
          }}
        />
      ) : null,
    },
    {
      title: i18n["Form.PersonalInfo.Section.Address"],
      fieldKeys: [FieldKey.ADDRESS, FieldKey.CITY, FieldKey.STATE, FieldKey.ZIP_CODE],
    },
  ];

  const extraProps = {
    email: {
      additionalClass: "medium",
    },
    cellPhoneNumber: {
      selectedCountry: getCountryFromPhoneNumberOrDefault(
        availableCountries,
        patientInformation.phoneNumbers.cellPhoneNumber,
        patientInformation.country
      ),
      availableCountries,
      additionalClass: "medium",
    },
    workPhoneNumber: {
      selectedCountry: getCountryFromPhoneNumberOrDefault(
        availableCountries,
        patientInformation.phoneNumbers.workPhoneNumber,
        patientInformation.country
      ),
      availableCountries,
      additionalClass: "medium",
    },
    homePhoneNumber: {
      selectedCountry: getCountryFromPhoneNumberOrDefault(
        availableCountries,
        patientInformation.phoneNumbers.homePhoneNumber,
        patientInformation.country
      ),
      availableCountries,
      additionalClass: "medium",
    },
    gender: {
      options: [Gender.MALE, Gender.FEMALE, Gender.OTHER].map(
        (gender) =>
          new SelectableOption(gender, i18n[getI18n(gender)], patientInformation.gender == gender)
      ),
    },
    zipCode: {
      country:
        availableCountries.find((country) => patientInformation.country == country.name) ||
        availableCountries[0],
      additionalClass: "small",
    },
    state: {
      additionalClass: "small",
    },
  };

  const initialValueAndExtraProps = fields.reduce((acc, field) => {
    const initialValue = findInformation(field.key, patientInformation);
    acc[field.name] = {
      initialValue,
      dirty: !!initialValue,
      ...extraProps[field.name],
    };
    return acc;
  }, {});

  return (
    <>
      {title && <FormTitle title={title} />}
      <FormWithSections
        submitLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
        submit={submitPatientInformation}
        generalErrorMsg={errorMessage}
      >
        {sections.map((section) => (
          <FormSection title={section.title} key={section.title}>
            {section.topContent}
            {Form.getFieldComponents(
              fields.filter((field) => section.fieldKeys.includes(field.key)),
              initialValueAndExtraProps,
              FieldKey.getTitle,
              getInputForUpdate,
              patientInformation.contactInfoUid
            )}
            {section.additionalContent}
          </FormSection>
        ))}
      </FormWithSections>
    </>
  );
}
